import { ScrollArea } from '@shadcn/ui';
import { InsightCardsPanel } from 'app/components/panels/InsightCardsPanel';
import { UsageTrendChart } from 'app/components/charts/UsageTrendChart';
import { UsageDegreeChart } from 'app/components/charts/UsageDegreeChart';
import { PeakDemandChart } from 'app/components/charts/PeakDemandChart';
import { getUsageUnit } from 'app/utils/formatters';
import { ALL, ELECTRIC } from 'app/utils/constants/utilityServices';
import { DataSource } from 'app/types/visualizations';
import { PenaltyData } from 'app/types/penalty-data';
import { Documents, WeatherProps } from 'app/types/visualizations';
import { PropertyMetrics, FlattenedAlert } from 'app/services/energy-star-services';
import { useState } from 'react';
import { EfficiencyMeasure } from 'app/types/efficiency-measures';
import { EfficiencyDashboard } from '../efficiency/EfficiencyDashboard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shadcn/ui/tabs';
import { Card, CardContent } from '@shadcn/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/ui';
import { FEATURE_FLAG_EFFICIENCY_MEASURES } from 'app/utils/constants/featureFlags';
import { EnergyOperations } from './EnergyOperations';

interface ServiceContentProps {
  serviceType: typeof ALL | typeof ELECTRIC | string;
  dataSource: DataSource;
  documents: Documents;
  meterReadings: Documents;
  trendDataByService: any;
  baseloads: any;
  meterReadingsBaseloads: any;
  baseTemperature: number;
  isUpdatingDegreeData: boolean;
  weather: WeatherProps;
  onBaseTemperatureChange: (value: number[]) => void;
  project: any;
  emissionsFromUtilityBills: PenaltyData | null;
  emissionsFromMeterReadings: PenaltyData | null;
  isEmissionsLoading: boolean;
  projectId: string;
  energyStarScoreByDateRange?: { [dateRange: string]: PropertyMetrics };
  noScoreReason?: FlattenedAlert[];
  theme: any;
}

// Sample efficiency measure
const sampleMeasure: EfficiencyMeasure = {
  id: '1',
  name: 'Chiller Upgrade',
  description: 'Replacement of old chiller system with high-efficiency model',
  startDate: new Date('2024-02-01'),
  endDate: new Date('2024-09-30'),
  impactedServices: {
    electric: true,
    gas: false,
    steam: false,
    oil: false,
    water: false,
  },
  expectedSavings: {
    electric: { amount: 150000, unit: 'kWh' },
  },
  baselinePeriod: {
    startDate: new Date('2023-06-01'),
    endDate: new Date('2024-02-01'),
  },
};

export const ServiceContent = ({
  serviceType,
  dataSource,
  documents,
  meterReadings,
  trendDataByService,
  baseloads,
  meterReadingsBaseloads,
  baseTemperature,
  isUpdatingDegreeData,
  weather,
  onBaseTemperatureChange,
  project,
  theme,
}: ServiceContentProps) => {
  const [efficiencyMeasures, setEfficiencyMeasures] = useState<EfficiencyMeasure[]>([sampleMeasure]);
  const [activeTab, setActiveTab] = useState<'overview' | 'efficiency'>('overview');
  const [selectedYear, setSelectedYear] = useState<number>(2025);

  // Years in descending order from 2025 to 2021
  const years = [2025, 2024, 2023, 2022, 2021];

  const handleAddMeasure = () => {
    // Implementation for adding new measure
    const newMeasure: EfficiencyMeasure = {
      id: String(efficiencyMeasures.length + 1),
      name: 'New Measure',
      description: 'Description of the new measure',
      startDate: new Date(),
      endDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000), // 180 days from now
      impactedServices: {
        electric: true,
        gas: false,
        steam: false,
        oil: false,
        water: false,
      },
      expectedSavings: {
        electric: { amount: 0, unit: 'kWh' },
      },
      baselinePeriod: {
        startDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000), // 180 days ago
        endDate: new Date(),
      },
    };
    setEfficiencyMeasures([...efficiencyMeasures, newMeasure]);
  };

  if (serviceType === ALL) {
    return (
      <ScrollArea orientation="vertical" className="grow p-4">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'overview' | 'efficiency')}>
          <div className="mb-4 flex items-center gap-4">
            <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(Number(value))}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              {FEATURE_FLAG_EFFICIENCY_MEASURES(project?.attributes?.name || '') && (
                <TabsTrigger value="efficiency">Efficiency Measures</TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="overview">
            <EnergyOperations
              isPortfolio={false}
              year={selectedYear}
              dataSource={dataSource}
              documents={documents}
              meterReadings={meterReadings}
            />
          </TabsContent>

          {FEATURE_FLAG_EFFICIENCY_MEASURES(project?.attributes?.name || '') && (
            <TabsContent value="efficiency">
              <EfficiencyDashboard
                project={project}
                measures={efficiencyMeasures}
                onAddMeasure={handleAddMeasure}
                utilityBillsData={trendDataByService[ELECTRIC]?.cdd?.trendDataByBillPeriod || []}
                meterReadingsData={trendDataByService[ELECTRIC]?.meterReadingsCdd?.trendDataByBillPeriod || []}
                theme={theme}
              />
            </TabsContent>
          )}
        </Tabs>
      </ScrollArea>
    );
  }

  return (
    <div>
      {/* Service-specific insights panel */}
      <Card className="mb-6 border-2">
        <CardContent>
          <InsightCardsPanel
            serviceType={serviceType}
            documents={documents}
            meterReadings={meterReadings}
            baseloads={baseloads}
            meterReadingsBaseloads={meterReadingsBaseloads}
            renderLoadMetric={getUsageUnit}
            selectedDataSource={dataSource as DataSource}
          />
        </CardContent>
      </Card>

      {dataSource === 'utility-bills' && (
        <div className="space-y-6">
          <UsageTrendChart
            utilityBillsData={trendDataByService[serviceType].cdd.trendDataByBillPeriod}
            meterReadingsData={[]}
            units={getUsageUnit(serviceType)}
            efficiencyMeasures={serviceType === ELECTRIC ? efficiencyMeasures : []}
          />

          <UsageDegreeChart
            coolingDegreeData={trendDataByService[serviceType].cdd.trendDataByBillPeriod}
            heatingDegreeData={trendDataByService[serviceType].hdd.trendDataByBillPeriod}
            meterReadingsCddData={[]}
            meterReadingsHddData={[]}
            baseloads={baseloads}
            meterReadingsBaseloads={meterReadingsBaseloads}
            serviceType={serviceType}
            baseTemperature={baseTemperature}
            onBaseTemperatureChange={onBaseTemperatureChange}
            isUpdatingDegreeData={isUpdatingDegreeData}
            renderLoadMetric={getUsageUnit}
          />

          {serviceType === ELECTRIC && (
            <PeakDemandChart
              utilityBillsData={trendDataByService[serviceType].cdd.trendDataByBillPeriod}
              weather={weather}
            />
          )}
        </div>
      )}

      {dataSource === 'energy-star' && (
        <div className="space-y-6">
          <UsageTrendChart
            utilityBillsData={[]}
            meterReadingsData={trendDataByService[serviceType].meterReadingsCdd.trendDataByBillPeriod}
            units={getUsageUnit(serviceType)}
            efficiencyMeasures={serviceType === ELECTRIC ? efficiencyMeasures : []}
          />

          <UsageDegreeChart
            coolingDegreeData={[]}
            heatingDegreeData={[]}
            meterReadingsCddData={trendDataByService[serviceType].meterReadingsCdd.trendDataByBillPeriod}
            meterReadingsHddData={trendDataByService[serviceType].meterReadingsHdd.trendDataByBillPeriod}
            baseloads={baseloads}
            meterReadingsBaseloads={meterReadingsBaseloads}
            serviceType={serviceType}
            baseTemperature={baseTemperature}
            onBaseTemperatureChange={onBaseTemperatureChange}
            isUpdatingDegreeData={isUpdatingDegreeData}
            renderLoadMetric={getUsageUnit}
          />
        </div>
      )}
    </div>
  );
};
